import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';

type Platform = 'windows' | 'mac';

/**
 * A key combo is written once with `Mod` for Ctrl/⌘ and `Alt` for Alt/⌥;
 * `formatKeys` turns it into the labels of the chosen platform.
 */
interface Shortcut {
  readonly keys: readonly (readonly string[])[];
  readonly labelKey: MessageKey;
}

interface ShortcutGroup {
  readonly id: string;
  readonly titleKey: MessageKey;
  readonly shortcuts: readonly Shortcut[];
}

const GROUPS: readonly ShortcutGroup[] = [
  {
    id: 'general',
    titleKey: 'help.shortcuts.groups.general',
    shortcuts: [
      { keys: [['Mod', 'Z']], labelKey: 'help.shortcuts.items.undo' },
      { keys: [['Mod', 'Y'], ['Mod', 'Shift', 'Z']], labelKey: 'help.shortcuts.items.redo' },
      { keys: [['Mod', 'F']], labelKey: 'help.shortcuts.items.find' },
      { keys: [['Mod', 'H']], labelKey: 'help.shortcuts.items.replace' },
      { keys: [['Esc']], labelKey: 'help.shortcuts.items.escape' },
    ],
  },
  {
    id: 'navigation',
    titleKey: 'help.shortcuts.groups.navigation',
    shortcuts: [
      { keys: [['↑'], ['↓'], ['←'], ['→']], labelKey: 'help.shortcuts.items.move' },
      { keys: [['Mod', '↑'], ['Mod', '↓'], ['Mod', '←'], ['Mod', '→']], labelKey: 'help.shortcuts.items.moveEdge' },
      { keys: [['Tab'], ['Shift', 'Tab']], labelKey: 'help.shortcuts.items.moveRight' },
      { keys: [['Enter'], ['Shift', 'Enter']], labelKey: 'help.shortcuts.items.moveDown' },
      { keys: [['Home'], ['End']], labelKey: 'help.shortcuts.items.rowEdge' },
      { keys: [['Mod', 'Home'], ['Mod', 'End']], labelKey: 'help.shortcuts.items.sheetEdge' },
      { keys: [['PageUp'], ['PageDown']], labelKey: 'help.shortcuts.items.page' },
    ],
  },
  {
    id: 'selection',
    titleKey: 'help.shortcuts.groups.selection',
    shortcuts: [
      { keys: [['Shift', '↑ ↓ ← →']], labelKey: 'help.shortcuts.items.extend' },
      { keys: [['Mod', 'Shift', '↑ ↓ ← →']], labelKey: 'help.shortcuts.items.extendEdge' },
      { keys: [['Mod', 'Space']], labelKey: 'help.shortcuts.items.selectColumn' },
      { keys: [['Space']], labelKey: 'help.shortcuts.items.toggleCheckbox' },
    ],
  },
  {
    id: 'editing',
    titleKey: 'help.shortcuts.groups.editing',
    shortcuts: [
      { keys: [['F2'], ['Enter']], labelKey: 'help.shortcuts.items.edit' },
      { keys: [['=']], labelKey: 'help.shortcuts.items.startFormula' },
      { keys: [['Delete'], ['Backspace']], labelKey: 'help.shortcuts.items.clear' },
      { keys: [['Mod', 'C']], labelKey: 'help.shortcuts.items.copy' },
      { keys: [['Mod', 'V']], labelKey: 'help.shortcuts.items.paste' },
      { keys: [['Mod', 'Shift', 'V']], labelKey: 'help.shortcuts.items.pasteValues' },
      { keys: [['Alt', 'Enter'], ['Shift', 'Enter']], labelKey: 'help.shortcuts.items.newline' },
      { keys: [['Esc']], labelKey: 'help.shortcuts.items.cancelEdit' },
    ],
  },
  {
    id: 'formulas',
    titleKey: 'help.shortcuts.groups.formulas',
    shortcuts: [
      { keys: [['Tab']], labelKey: 'help.shortcuts.items.acceptSuggestion' },
      { keys: [['↑ ↓ ← →']], labelKey: 'help.shortcuts.items.pointReference' },
      { keys: [['F2']], labelKey: 'help.shortcuts.items.toggleArrowMode' },
      { keys: [['Mod', 'Space']], labelKey: 'help.shortcuts.items.widenColumn' },
      { keys: [['Mod', 'Enter']], labelKey: 'help.shortcuts.items.commitMultiline' },
    ],
  },
  {
    id: 'media',
    titleKey: 'help.shortcuts.groups.media',
    shortcuts: [
      { keys: [['←'], ['→']], labelKey: 'help.shortcuts.items.mediaNavigate' },
      { keys: [['+'], ['-'], ['0']], labelKey: 'help.shortcuts.items.mediaZoom' },
      { keys: [['Esc']], labelKey: 'help.shortcuts.items.mediaClose' },
    ],
  },
];

const MAC_KEYS: Readonly<Record<string, string>> = {
  Mod: '⌘',
  Alt: '⌥',
  Shift: '⇧',
  Enter: '↩',
  Backspace: '⌫',
  Delete: '⌦',
  Tab: '⇥',
};

const WINDOWS_KEYS: Readonly<Record<string, string>> = { Mod: 'Ctrl' };

function detectPlatform(): Platform {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const name = nav.userAgentData?.platform ?? nav.platform ?? nav.userAgent;
  return /mac|iphone|ipad|ipod/i.test(name) ? 'mac' : 'windows';
}

function formatKey(key: string, platform: Platform) {
  return (platform === 'mac' ? MAC_KEYS : WINDOWS_KEYS)[key] ?? key;
}

export function ShortcutsSection() {
  const { t } = useI18n();
  const [platform, setPlatform] = useState<Platform>(detectPlatform);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">{t('help.shortcuts.intro')}</p>
        <div role="radiogroup" aria-label={t('help.shortcuts.platform')} className="inline-flex rounded-full border border-border p-0.5">
          {(['windows', 'mac'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={platform === option}
              onClick={() => setPlatform(option)}
              className={`rounded-full px-3 py-1 text-sm transition-colors ${
                platform === option ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:text-fg'
              }`}
            >
              {t(option === 'mac' ? 'help.shortcuts.mac' : 'help.shortcuts.windows')}
            </button>
          ))}
        </div>
      </div>

      {GROUPS.map((group) => (
        <Card key={group.id} title={t(group.titleKey)}>
          <ul className="divide-y divide-border">
            {group.shortcuts.map((shortcut) => (
              <li key={shortcut.labelKey} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <span className="text-sm text-fg">{t(shortcut.labelKey)}</span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {shortcut.keys.map((combo, comboIndex) => (
                    <span key={comboIndex} className="inline-flex items-center gap-1">
                      {comboIndex > 0 && <span className="mr-1 text-xs text-muted">/</span>}
                      {combo.map((key) => (
                        <kbd
                          key={key}
                          className="min-w-[1.75rem] rounded-md border border-border bg-surface-3 px-1.5 py-0.5 text-center font-mono text-xs text-fg"
                        >
                          {formatKey(key, platform)}
                        </kbd>
                      ))}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
